import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { instance } from '@/shared/api/instance';

type ApiInterceptorCtx = {
  serverError500: boolean;
  setServerError500: (v: boolean) => void;
};

const Ctx = createContext<ApiInterceptorCtx | null>(null);

export const useApiInterceptor = () => {
  const ctx = useContext(Ctx);
  if (!ctx)
    throw new Error(
      'useApiInterceptor must be used within ApiInterceptorProvider'
    );
  return ctx;
};

export const ApiInterceptorProvider = ({ children }: PropsWithChildren) => {
  const [serverError500, setServerError500] = useState(false);

  const set500 = useCallback((v: boolean) => setServerError500(v), []);

  useEffect(() => {
    const resInterceptor = instance.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        if (status === 500) {
          setServerError500(true);
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      instance.interceptors.response.eject(resInterceptor);
    };
  }, [set500]);

  const value = useMemo(
    () => ({ serverError500, setServerError500: set500 }),
    [serverError500, set500]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
